const PGAdapter = require('../../common/pg-adapter')
const isPGUsageErrorCode = require('../../common/isPGUsageErrorCode')
const getLocationSkuHistoryQuery = require('./queries/get-location-sku-history-query')
const getReservationQuery = require('./queries/get-reservation-query')
const getLedgerQuery = require('./queries/get-ledger-query')
const calculateLocationLedgerQuery = require('./queries/calculate-location-ledger-query')
const { formatLedgerBalance } = require('../utils')

const isUuid = (value) => (
  typeof value === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
)

class LedgerPGAdapter extends PGAdapter {
  constructor ({pgConnection, username, logger}) {
    const columns = [
      'created_at',
      'updated_at',
      'id',
      'location_id',
      'sku',
      'event_id',
      'partner_balance',
      'sl_balance',
      'date',
      'is_stockcount',
      'ledger_id',
      '_deleted'
    ]
    super(pgConnection, 'avocado.couch_ledgers', username, columns, 'id')
    this.logger = logger
  }

  async getOne (locationFsid, opts = {includeShipments: false, includeOrders: false, programId: null}) {
    // If FSID convert to UUID
    const locationUuid = await this.getLocationUUID(locationFsid)
    const { includeShipments, includeOrders, programId } = opts

    let row = { ret_stock: {} }
    try {
      const { rows } = await this.pgConnection.query(getLedgerQuery, [
        locationUuid,
        programId
      ])
      if (rows.length) {
        row = rows[0]
        const finalAmounts = row.ret_stock.reduce((acc, item) => {
          acc[`product:${item.sku}`] = item.amount
          if (includeShipments) {
            acc[`product:${item.sku}`] += item.shipments || 0
          }
          if (includeOrders) {
            acc[`product:${item.sku}`] += item.orders || 0
          }
          return acc
        }, {})
        row.ret_stock = finalAmounts
      }
    } catch (err) {
      if (isPGUsageErrorCode(err.code)) {
        err.status = 400
      }
      throw err
    }
    return row.ret_stock
  }

  /**
   * Calculates the ledger balance for a location for a particular date
   * @param {Object} locationUuid - The location uuid.
   * @param {Object} date - The date we want to calculate the ledger for, defaults to now.
   * @returns 'When formatLedger is true Sku amount object {'product:5001': 8, 'product:5002': 5}'
   */

  async calculateLocationLedger (locationUuid, date = new Date().toJSON(), formatLedger = true) {
    const locationFsid = await this.getLocationFSID(locationUuid)

    const row = { ledger: {}, addedShipments: [], baseReport: {} }

    try {
      const { rows } = await this.pgConnection.query(calculateLocationLedgerQuery, [
        locationFsid, date
      ])
      if (rows.length) {
        const queryRow = rows[0]

        // Get ledger values
        row.ledger = formatLedger
          ? formatLedgerBalance(queryRow.stock)
          : rows[0].stock

        // Get ledger base Report
        row.baseReport = queryRow.base_report && queryRow.base_report_updated_at
          ? row.baseReport = { _id: queryRow.base_report, updatedAt: queryRow.base_report_updated_at }
          : {}

        // Get ledger relevant shipments
        row.addedShipments = (queryRow.added_shipments || []).map(s => {
          return { snapshotId: s }
        })
      }
    } catch (err) {
      if (isPGUsageErrorCode(err.code)) {
        err.status = 400
      }
      throw err
    }
    return row
  }

  async getLocationSkuHistory (locationId, productId) {
    let transformedProductId = productId

    if (productId.includes('product:')) {
      transformedProductId = productId.replace('product:', '')
    }

    let results = []
    try {
      const {rows} = await this.pgConnection.query(getLocationSkuHistoryQuery, [locationId, transformedProductId])
      if (rows.length) {
        results = rows
      }
    } catch (err) {
      if (isPGUsageErrorCode(err.code)) {
        err.status = 400
      }
      throw err
    }
    return results
  }

  async getSkuReservations (locationId, productId) {
    // If FSID convert to UUID
    const transformedLocationId = await this.getLocationUUID(locationId)

    let transformedProductId = productId

    if (productId.includes('product:')) {
      transformedProductId = productId.replace('product:', '')
    }

    // Set a default to zero for when theres no data i.e no shipments or orders with this sku
    let result = {
      shipments: 0,
      orders: 0,
      total: 0,
      rows: []
    }

    try {
      const {rows} = await this.pgConnection.query(getReservationQuery, [transformedLocationId, transformedProductId, locationId])
      if (rows.length) {
        const row = rows[0]
        const { shipment_reservations: shipmentReservations, order_reservations: orderReservations } = row
        const reservationRows = [...shipmentReservations || [], ...orderReservations || []]

        const transformedRows = reservationRows.reduce((acc, row) => {
          const { type } = row
          if (!type) return acc

          const reservationObject = {
            location: row.destination,
            quantity: row.amount,
            type,
            id: type === 'shipment'
              ? row.shipmentId
              : row.orderId
          }

          acc.push(reservationObject)
          return acc
        }, [])

        result.shipments = parseInt(row.shipments) || 0
        result.orders = parseInt(row.orders) || 0
        result.total = result.shipments + result.orders
        result.rows = transformedRows
      }
    } catch (err) {
      if (isPGUsageErrorCode(err.code)) {
        err.status = 400
      }
      throw err
    }
    return result
  }

  async getLocationUUID (locationId) {
    if (isUuid(locationId)) {
      return locationId
    }
    try {
      const {rows} = await this.pgConnection.query('SELECT uuid FROM avocado.data_location WHERE fsid = $1', [locationId])

      if (rows.length === 0) {
        const err = new Error('Location not found')
        err.status = 404
        throw err
      }

      return `${rows[0].uuid}`
    } catch (err) {
      if (isPGUsageErrorCode(err.code)) {
        err.status = 400
      }
      throw err
    }
  }

  async getLocationFSID (locationId) {
    try {
      const {rows} = await this.pgConnection.query('SELECT fsid FROM avocado.data_location WHERE uuid = $1', [locationId])

      if (rows.length === 0) {
        const err = new Error('Location not found')
        err.status = 404
        throw err
      }

      return `${rows[0].fsid}`
    } catch (err) {
      if (isPGUsageErrorCode(err.code)) {
        err.status = 400
      }
      throw err
    }
  }

  async create ({sku, date}) {
    throw new Error('Ledger cannot be modified directly')
  }
  async delete () {
    throw new Error('Ledger cannot be modified directly')
  }
  async update () {
    throw new Error('Ledger cannot be modified directly')
  }
}

module.exports = {LedgerPGAdapter}
